const SHELL_URL = import.meta.env.VITE_DRAGNET_ERP_URL as string;

export interface UserInfo {
  name: string;
  email: string;
  roles: string[];
  expiresOn: number;
}

/**
 * Ask the server if the shared erp_auth cookie is still valid. The ERP backend is the
 * only thing that ever sets this cookie (Domain=.dragnet.ng) — a raw token never
 * reaches this app's JS or URL. Used on every standalone boot, and covers new tabs
 * and fresh visits where the cookie is already present.
 */
export async function verifySession(): Promise<UserInfo | null> {
  try {
    const res = await fetch('/api/auth/session', { credentials: 'include' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * No valid session — redirect to the ERP shell with the current URL as the return
 * destination. The shell authenticates (or reuses its own MSAL session), the ERP
 * backend sets the shared cookie, and it redirects back here with no token in the URL.
 */
export function redirectToShell(): void {
  const currentUrl = window.location.href;
  window.location.href = `${SHELL_URL}?redirect=${encodeURIComponent(currentUrl)}`;
}

/** Clear the shared cookie without a full sign-out — used before bouncing back to the ERP for a fresh session. */
export function clearSession(): void {
  fetch('/api/auth/session', { method: 'DELETE', credentials: 'include' }).catch(() => {});
}

/** Sign out: clear the cookie, then hand off to ERP logout so MSAL and Azure AD sign out too. */
export function logoutToShell(): void {
  clearSession();
  window.location.href = `${SHELL_URL}/logout`;
}
